import {
  accordionClasses,
  accordionDetailsClasses,
  accordionSummaryClasses,
  Accordion as MuiAccordion,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  CHEVRON_COLOR,
  CHEVRON_SIZE,
  DETAILS_PADDING_TOP,
  EXPANDED_CHEVRON_COLOR,
  HEADER_WIDTH,
  TITLE_STYLES,
} from "ui/accordion/accordion.constants";
import { type AccordionVariant } from "ui/accordion/accordion.types";

/**
 * Every child rule is written from the root rather than as its own `styled()` call: from here each
 * selector carries the root's Emotion class on top of MUI's single slot class, so it out-ranks the
 * slot's own declarations on specificity instead of on insertion order.
 */
export const AccordionRoot = styled(MuiAccordion, {
  shouldForwardProp: (prop) => prop !== "accordionVariant",
})<{ accordionVariant: AccordionVariant }>(({ theme, accordionVariant }) => ({
  /**
   * `Paper` paints a white surface and pins `color: text.primary` (navy-900). This disclosure has
   * never had a surface of its own, and both its title and its panel take the page's ink.
   */
  backgroundColor: "transparent",
  color: "inherit",
  width: "100%",

  /** `Paper`'s divider above every sibling but the first — three of these render in a row. */
  "&::before": {
    display: "none",
  },

  /** A disabled accordion is washed with `action.disabledBackground`; only the header should fade. */
  [`&.${accordionClasses.disabled}`]: {
    backgroundColor: "transparent",
  },

  [`& .${accordionSummaryClasses.root}`]: {
    width: HEADER_WIDTH[accordionVariant],
    minHeight: 0,
    padding: 0,

    /**
     * The header is a `ButtonBase`, and nothing in MUI resets a `<button>`'s UA font. Tailwind's
     * preflight does, and that is what these titles have been inheriting; stating it here keeps the
     * inherited family and size once preflight stops being compiled.
     */
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    letterSpacing: "inherit",
  },

  [`& .${accordionSummaryClasses.content}`]: {
    margin: 0,
    ...TITLE_STYLES[accordionVariant](theme),
  },

  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    color: CHEVRON_COLOR(theme),
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shorter,
    }),

    /** The glyph is a right-pointing caret, so a quarter turn points it down; MUI's own is 180°. */
    [`&.${accordionSummaryClasses.expanded}`]: {
      transform: "rotate(90deg)",
      color: EXPANDED_CHEVRON_COLOR[accordionVariant]?.(theme),
    },

    "& svg": {
      width: CHEVRON_SIZE,
      height: CHEVRON_SIZE,
    },
  },

  [`& .${accordionDetailsClasses.root}`]: {
    padding: theme.spacing(DETAILS_PADDING_TOP[accordionVariant], 0, 0),
  },
}));
